import { computed, inject, Injector, signal } from "@angular/core";
import { CommonDateTimeField, CommonDateTimeFieldConfig, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField, CommonUrlField, CommonUrlFieldConfig, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { AppService } from "../../../../../services";
import { PersonDialog } from "../../../../../ui/widget/person-dialog";
import { EventAddDialogDataService } from "../event-add-dialog-data.service";

export interface EventAddFormValue {
	startUTC: number,
	endUTC: number,
	toolSelection: 'automatic' | 'custom',
	toolStartUTC?: number,
	toolEndUTC?: number,
	link?: string,
	publicNotes?: string,
	venue: 'In Person' | 'Online',
}

export const MINUTE = 60;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export class EventAddFormService {

	public readonly form = new CommonForm<EventAddFormValue>(inject(Injector));
	public readonly dialogService = inject(DialogService);
	public readonly util = inject(UtilityService);
	private app = inject(AppService);
	public eventAddDialogDataService = inject(EventAddDialogDataService);
	public canEdit = computed(() => this.app.data().permissions.canFacilitateCohorts);

	private userStaff = computed(() => this.app.data().staff.find(staff => staff.staffId == this.app.data().user.userId));
	public hostPerson = signal<{ personId: number, fullName: string } | undefined>(this.getHostPerson());
	public venueValues = signal<{ venueId: number, displayName: string } | undefined>(undefined);

	readonly startDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({ label: 'Start', isRequired: true, isSpanish: false, isEnabled: computed(() => this.canEdit()), type: 'event', includeTime: true }),
		actualValue: computed(() => undefined),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'startUTC',
		form: this.form,
	}));

	endDateMin = computed(() => this.startDate().pendingValue() || this.startDate().actualValue());
	readonly endDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'End',
			minDateUTC: this.endDateMin() ? this.endDateMin()! + MINUTE : this.endDateMin(),
			isRequired: true, isSpanish: false, type: 'event', includeTime: true, isEnabled: computed(() => this.canEdit())
		}),
		actualValue: computed(() => this.endDateMin() ? this.endDateMin()! + 2 * HOUR : undefined),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'endUTC',
		form: this.form,
	}));


	private readonly toolOptions = computed(() => [
		{ text: "24 hours before event to 24 hours after", value: "automatic" },
		{ text: "Custom", value: "custom" },
	]);

	public readonly toolEnabledSelection = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: '',
			options: this.toolOptions(),
			isRequired: !!this.eventAddDialogDataService.data()?.eventType.toolNameLabelKey,
			isSpanish: false,
			isEnabled: computed(() => this.canEdit()),
		}),
		actualValue: computed(() => 'automatic'),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolSelection',
		form: this.form,
	}));

	public toolSelectionValue = computed(() => this.toolEnabledSelection().pendingValue() || this.toolEnabledSelection().actualValue());

	readonly toolStartDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Tool Start',
			isRequired: !!this.eventAddDialogDataService.data()?.eventType.toolNameLabelKey,
			isSpanish: false,
			isEnabled: computed(() => this.canEdit()),
			type: 'event',
			includeTime: true
		}),
		actualValue: computed(() => undefined),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolStartUTC',
		form: this.form,
	}));

	toolEndDateMin = computed(() => this.toolStartDate().pendingValue() || this.toolStartDate().actualValue());

	readonly toolEndDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Tool End',
			minDateUTC: this.toolEndDateMin() ? this.toolEndDateMin()! + MINUTE : this.toolEndDateMin(),
			isRequired: !!this.eventAddDialogDataService.data()?.eventType.toolNameLabelKey, isSpanish: false, type: 'event', includeTime: true, isEnabled: computed(() => this.canEdit())
		}),
		actualValue: computed(() => undefined),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'toolEndUTC',
		form: this.form,
	}));


	readonly duration = computed(() => {
		const startUTC = this.startDate().pendingValue() || this.startDate().actualValue();
		const endUTC = this.endDate().pendingValue() || this.endDate().actualValue();
		return startUTC && endUTC ? this.util.date.calculateTimePeriod(startUTC, endUTC) : undefined;
	});

	readonly toolDuration = computed(() => {
		const startUTC = this.toolStartDate().pendingValue() || this.toolStartDate().actualValue();
		const endUTC = this.toolEndDate().pendingValue() || this.toolEndDate().actualValue();
		return startUTC && endUTC ? this.util.date.calculateTimePeriod(startUTC, endUTC) : undefined;
	});



	private readonly venueOptions = computed(() => [
		{
			text: 'In Person',
			value: 'In Person',
			disabled: false,
		},
		{
			text: 'Online',
			value: 'Online',
			disabled: false,
		}
	]);

	public readonly venue = computed<CommonSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Venue',
			options: this.venueOptions(),
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => this.canEdit()
			),
		}),
		actualValue: computed(() => 'Online'),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'venue',
		form: this.form,
	}));

	public venueValue = computed(() => this.venue().pendingValue() || this.venue().actualValue());



	readonly publicNotes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Public Notes', min: 0, max: 200, fixCase: 'sentence-case', isRequired: false, multiLine: true, isSpanish: false, isEnabled: computed(() => this.canEdit()) }),
		actualValue: computed(() => ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'publicNotes',
		form: this.form,
	}));


	readonly link = computed<CommonUrlField>(() => ({
		config: signal<CommonUrlFieldConfig>({
			label: 'Link',
			isRequired: false,
			isEnabled: computed(() => this.canEdit())
		}),
		formFieldName: 'link',
		form: this.form,
		actualValue: computed(() => ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	public handleStaffValueChange(value: { personId?: number, fullName?: string } | undefined) {
		if (value?.personId && value?.fullName) this.hostPerson.set({ personId: value.personId, fullName: value.fullName });
	};

	private getHostPerson() {
		const acc = this.eventAddDialogDataService.data()?.acc;
		if (acc) {
			return { personId: acc.facilitatorPersonId, fullName: acc.facilitatorName }
		}
		else return this.userStaff() && this.userStaff()!.personId ? { personId: this.userStaff()!.personId!, fullName: this.userStaff()!.fullName } : undefined
	}



	public async openPersonDialog() {
		const hostPerson = this.hostPerson();
		if (!hostPerson) return;
		await this.dialogService.showCustom(PersonDialog, { data: { personId: hostPerson.personId, toEdit: false } }, 800, 800);
	}
}